import { parseGPX, MetaData } from "@we-gold/gpxjs";
import {
  Coordinate,
  reduceCoordinatesFavouringDistance,
} from "./use-coordinates";

interface UseGPXParserProps {}

export const useGPXParser = (props: UseGPXParserProps) => {
  const parse = (gpx: string) => {
    const [parsedFile, error] = parseGPX(gpx);

    if (error) throw error;

    const geoJSON = parsedFile.toGeoJSON();
    return geoJSON;
  };

  return {
    parse,
  };
};

const parseGPXToGeoJSON = (gpx: string) => {
  const [parsedFile, error] = parseGPX(gpx);

  if (error) throw error;
  var geoJSON = parsedFile.toGeoJSON();

  const properties: MetaData = {
    name: parsedFile.tracks[0].name,
    description: parsedFile.tracks[0].description,
    time: parsedFile.metadata.time,
    author: parsedFile.metadata.author,
    link: parsedFile.metadata.link,
  };
  geoJSON.properties = properties;

  return geoJSON;
};

export const getCoordinatesFromGPXStringX = (
  gpxString: string,
  desiredLength?: number
): { name: string | undefined; coordinates: Coordinate[] } => {
  const geoJSON = parseGPXToGeoJSON(gpxString);
  const name = geoJSON.properties.name ?? undefined;

  const coordinates = geoJSON.features[0].geometry.coordinates;
  // Each coordinate is an array of [lng, lat, eleveation]
  const array: Coordinate[] = coordinates
    .map((coord) => {
      const array = coord as number[];
      if (array.length > 1) {
        return { lat: array[1], lng: array[0] };
      }
      return null;
    })
    .filter((coord): coord is Coordinate => coord !== null);
  if (desiredLength) {
    const paredDown = reduceCoordinatesFavouringDistance(array, desiredLength);
    return { name, coordinates: paredDown };
  }
  return { name, coordinates: array };
};

export const getCoordinatesFromGPXString = (
  gpxString: string,
  desiredLength?: number
): { name: string | undefined; coordinates: Coordinate[] } => {
  const [parsedFile, error] = parseGPX(gpxString);
  if (error) throw error;

  const name = parsedFile.tracks[0].name ?? undefined;

  // Flatten all segments of all tracks
  const allTrackPoints = parsedFile.tracks.flatMap((track) => track.points);

  let coordinates: Coordinate[] = allTrackPoints.map((pt) => ({
    lat: pt.latitude,
    lng: pt.longitude,
    elevation: pt.elevation ?? undefined,
    time: pt.time?.getTime(), // or just `pt.time` if you want a Date
  }));

  if (desiredLength) {
    coordinates = reduceCoordinatesFavouringDistance(
      coordinates,
      desiredLength
    );
  }
  return { name, coordinates };
};
